import {Modal} from '#/atoms/modal';
import {NoticeMessageErrorDetails} from '#/atoms/notice-message-error-details';
import {testnetAccountFundingRequests} from '#/components/testnet-account-funding/repo/fetch-testnet-account-funding';
import {
  TokenTransferSingleStepButton,
  TokenTransferStatusContext,
  TokenTransferStatusProvider,
  TokenTransferStatusTitle,
} from '#/components/wallet-transfer/wallet-transfer-modal';
import {ModalContext} from '#/contexts/modal-provider';
import {WalletContext} from '#/contexts/wallet-provider';
import {useNativeTokenBalance} from '#/hooks/query/use-native-token-balance';
import {testnetAccountFundingTargetAmounts} from '@shareland/shareland-common';
import {useMutation} from '@tanstack/react-query';
import {FunctionComponent, useContext} from 'react';
import {useTranslation} from 'react-i18next';

export const AddNativeTokensModal: FunctionComponent<{
  automatic: boolean;
}> = ({automatic}) => {
  const {close} = useContext(ModalContext);

  const {t} = useTranslation('translation');

  const {
    accountResult: {address: account, chain},
  } = useContext(WalletContext);

  const {data: nativeTokenBalance, refetch: refetchNativeTokenBalance} = useNativeTokenBalance();

  const canFundEth = nativeTokenBalance < testnetAccountFundingTargetAmounts.maxNative;
  const nativeTransferEnabled = !!account && canFundEth;
  const nativeTransferMutation = useMutation({
    mutationKey: ['fund-account-transfer-native'],
    mutationFn: async () => {
      if (!nativeTransferEnabled) {
        return;
      }
      const response = await testnetAccountFundingRequests.native({account: account!});

      refetchNativeTokenBalance();

      return response;
    },
  });

  const nativeTokenSymbol = chain?.nativeCurrency.symbol ?? '';

  return (
    <Modal title={t('pages.addNativeTokens.title')} onClose={close} className='rounded-xl'>
      <div className='p-4 w-[340px]'>
        <TokenTransferStatusProvider
          queryEnabled={nativeTransferEnabled}
          queryStatus={nativeTransferMutation.status}
          queryError={nativeTransferMutation.error as Error | null}
          queryStart={() => {
            nativeTransferMutation.mutate();
          }}
        >
          <div className='space-y-2'>
            <div className='text-sm font-normal text-black/80'>
              {canFundEth
                ? automatic
                  ? t('pages.addNativeTokens.explanationCanFundInitAutomatic', {
                      nativeTokenSymbol,
                    })
                  : t('pages.addNativeTokens.explanationCanFundInitUserAction', {
                      nativeTokenSymbol,
                    })
                : t('pages.addNativeTokens.explanationCannotFund', {
                    nativeTokenSymbol,
                    maxAmount: Number(testnetAccountFundingTargetAmounts.maxNative) / 10 ** 12,
                  })}
            </div>

            <div className={'h-px w-full bg-gray-600 opacity-20'} />

            <TokenTransferStatusTitle>
              {t('pages.addNativeTokens.tokenTransferStatusTitle', {
                nativeTokenSymbol,
              })}
            </TokenTransferStatusTitle>

            <TokenTransferSingleStepButton />

            <TokenTransferStatusContext.Consumer>
              {({error}) => <> {!!error && <NoticeMessageErrorDetails error={error as Error} />}</>}
            </TokenTransferStatusContext.Consumer>
          </div>
        </TokenTransferStatusProvider>
      </div>
    </Modal>
  );
};
